import { MicIcon, Pause, Square } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { LoaderCircle } from "~/components/animate-ui/icons/loader-circle";
import { Button } from "~/components/ui/button";
import { toast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import {
  SpeechRecognition,
  SpeechRecognitionErrorEvent,
  SpeechRecognitionEvent,
} from "~/types/speechRecognition";
import { trackEvent } from "~/utils/analytics";

interface AudioRecordingButtonProps {
  className?: string;
  onRecordingStateChange?: (isRecording: boolean) => void;
  isProcessingImprovement: boolean;
  isLoading?: boolean;
  onAutoImproveChunk: (rawTranscriptChunk: string) => void;
  onRecordingStop: () => Promise<void>;
  isProcessingQueue: boolean;
  onTextSpeechUpdate: (text: string) => void;
}

export const AudioRecordingButton: FC<AudioRecordingButtonProps> = ({
  className,
  isProcessingImprovement,
  isLoading,
  onRecordingStateChange,
  onAutoImproveChunk,
  onRecordingStop,
  isProcessingQueue,
  onTextSpeechUpdate,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [, setVolume] = useState<number>(0);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const transcriptRef = useRef<string>("");
  const interimTranscriptRef = useRef<string>(""); // Store interim results
  const isRecordingRef = useRef<boolean>(false);
  const isPausedRef = useRef<boolean>(false);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastAutoSaveTimeRef = useRef<number>(0);
  const speechTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    onRecordingStateChange?.(isRecording);
    isRecordingRef.current = isRecording;
  }, [isRecording, onRecordingStateChange]);

  useEffect(() => {
    return () => {
      onAutoImproveChunk(interimTranscriptRef.current);

      // Clean up recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current = null;
        } catch (error) {
          console.error("Error stopping recognition on unmount:", error);
        }
      }

      // Clean up auto-save interval
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }

      // Clean up speech timeout
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }

      setIsRecording(false);
      setIsPaused(false);
      setVolume(0);
    };
  }, []);

  const showToastMessage = useCallback((title: string, message: string) => {
    toast({
      variant: "default",
      title: title,
      description: message,
    });
  }, []);

  const saveTranscriptContent = useCallback(() => {
    if (transcriptRef.current.trim()) {
      const chunk = transcriptRef.current.trim();
      onAutoImproveChunk(chunk);
      transcriptRef.current = "";
    }
  }, [onAutoImproveChunk]);

  const startAutoSave = useCallback(() => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
    }
    autoSaveIntervalRef.current = setInterval(() => {
      // Send every 5 seconds if user is still recording and has content
      if (isRecordingRef.current) {
        const finalText = transcriptRef.current.trim();

        if (finalText) {
          onAutoImproveChunk(finalText);
          transcriptRef.current = "";
          lastAutoSaveTimeRef.current = Date.now();
        }
      }
    }, 5000);
  }, [onAutoImproveChunk]);

  const stopAutoSave = useCallback(() => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }
  }, []);

  const requestMicrophonePermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          showToastMessage(
            "Permission Denied",
            "Please allow microphone access to use voice recording."
          );
        } else if (error.name === "NotFoundError") {
          showToastMessage(
            "No Microphone",
            "No microphone found. Please connect a microphone and try again."
          );
        } else {
          showToastMessage(
            "Microphone Error",
            "Unable to access microphone. Please check your settings."
          );
        }
      }
      return false;
    }
  }, [showToastMessage]);

  const initializeSpeechRecognition = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      showToastMessage(
        "Error",
        "Speech recognition is not supported in this browser. Please use Chrome, Safari, or Edge."
      );
      return null;
    }

    const recognition: SpeechRecognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const newVolume = Math.min(0.5 + Math.random() * 0.7, 1);
      setVolume(newVolume);
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + " ";
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        transcriptRef.current += finalTranscript;
      }

      interimTranscriptRef.current = interimTranscript;
      onTextSpeechUpdate(
        transcriptRef.current.length
          ? transcriptRef.current
          : interimTranscriptRef.current
      );

      setTimeout(() => {
        setVolume(0);
      }, 200);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === "no-speech") return;

      if (isRecordingRef.current) {
        setIsRecording(false);
        isRecordingRef.current = false;
        setIsPaused(false);
        isPausedRef.current = false;
      }

      const messages: Record<string, string> = {
        "not-allowed": "Please allow microphone access and try again.",
        network: "Network error occurred. Please check your connection.",
      };

      showToastMessage(
        "Speech Recognition Error",
        messages[event.error] ?? "An unexpected error occurred."
      );
    };

    recognition.onend = () => {
      // Guard against stale onend from a previous recognition instance
      // (e.g. after Pause→Resume or Pause→Stop sequences)
      if (recognition !== recognitionRef.current) {
        return;
      }
      if (isPausedRef.current) {
        saveTranscriptContent();
        return;
      }
      if (isRecordingRef.current) {
        setTimeout(() => {
          if (
            recognitionRef.current === recognition &&
            isRecordingRef.current &&
            !isPausedRef.current
          ) {
            recognitionRef.current.start();
          }
        }, 100);
      } else {
        stopAutoSave();
        saveTranscriptContent();
        onRecordingStop();
      }
    };

    return recognition;
  }, [showToastMessage, stopAutoSave, onRecordingStop, saveTranscriptContent]);

  const handleStart = useCallback(async () => {
    trackEvent({ eventName: "app/notes/record_audio.button/click" });

    const hasPermission = await requestMicrophonePermission();
    if (!hasPermission) {
      return;
    }

    const recognition = initializeSpeechRecognition();
    if (recognition) {
      recognitionRef.current = recognition;
      setIsRecording(true);
      isRecordingRef.current = true;
      setIsPaused(false);
      isPausedRef.current = false;
      lastAutoSaveTimeRef.current = Date.now();
      try {
        recognition.start();
      } catch (error) {
        showToastMessage(
          "Speech Recognition Error",
          "Failed to start speech recognition. Please try again."
        );
        setIsRecording(false);
        isRecordingRef.current = false;
        return;
      }
      startAutoSave();
    }
  }, [
    initializeSpeechRecognition,
    requestMicrophonePermission,
    startAutoSave,
    showToastMessage,
  ]);

  const handlePause = useCallback(() => {
    trackEvent({ eventName: "app/notes/record_audio.pause/click" });

    setIsPaused(true);
    isPausedRef.current = true;
    stopAutoSave();
    saveTranscriptContent();

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {}
    }
    setVolume(0);
  }, [stopAutoSave, saveTranscriptContent]);

  const handleResume = useCallback(async () => {
    trackEvent({ eventName: "app/notes/record_audio.resume/click" });

    const recognition = initializeSpeechRecognition();
    if (recognition) {
      recognitionRef.current = recognition;
      try {
        recognition.start();
      } catch (error) {
        showToastMessage(
          "Speech Recognition Error",
          "Failed to resume speech recognition. Please try again."
        );
        return;
      }
      setIsPaused(false);
      isPausedRef.current = false;
      startAutoSave();
    }
  }, [initializeSpeechRecognition, startAutoSave, showToastMessage]);

  const handleStop = useCallback(() => {
    trackEvent({ eventName: "app/notes/record_audio.button/click" });

    const wasPaused = isPausedRef.current;

    setIsRecording(false);
    isRecordingRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;

    stopAutoSave();

    if (!wasPaused && recognitionRef.current) {
      saveTranscriptContent();
      try {
        recognitionRef.current.stop();
      } catch (error) {}
    } else {
      // Null out the ref so any pending onend from the paused recognition
      // instance is treated as stale and doesn't call onRecordingStop() again
      recognitionRef.current = null;
      saveTranscriptContent();
      onRecordingStop();
    }

    setVolume(0);
  }, [stopAutoSave, saveTranscriptContent, onRecordingStop]);

  const isProcessing = isProcessingImprovement || isProcessingQueue;
  const isDisabled =
    !isRecording && (isProcessingImprovement || isLoading || isProcessingQueue);

  // Processing improvement is handled by the parent (FancyLoader in NotesArea),
  // so hide this button entirely to avoid rendering a duplicate bubble.
  if (isProcessingImprovement) {
    return null;
  }

  // Active recording state: indicator + pause button + stop button
  if (isRecording && !isPaused) {
    return (
      <div
        className={cn(
          "absolute left-2 transition-all duration-300 ease-in-out",
          className
        )}
      >
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-red-600 select-none">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
            <FormattedMessage
              id="notes.recording.recording"
              defaultMessage="Recording"
            />
          </span>
          <Button
            size="mini"
            onClick={handlePause}
            variant="default"
            className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3 z-10 relative"
            aria-label="Pause"
          >
            <Pause className="w-3.5 h-3.5" />
            <span className="text-xs">
              <FormattedMessage
                id="notes.recording.pause"
                defaultMessage="Pause"
              />
            </span>
          </Button>
          <Button
            size="mini"
            onClick={handleStop}
            variant="destructive"
            className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3 z-10 relative"
            aria-label="Stop"
          >
            <Square fill="white" className="w-3.5 h-3.5" />
            <span className="text-xs">
              <FormattedMessage
                id="notes.recording.stop"
                defaultMessage="Stop"
              />
            </span>
          </Button>
        </div>
      </div>
    );
  }

  // Paused state: indicator + resume button + stop button
  if (isRecording && isPaused) {
    return (
      <div
        className={cn(
          "absolute left-2 transition-all duration-300 ease-in-out",
          className
        )}
      >
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600 select-none">
            <span className="inline-flex rounded-full h-2 w-2 bg-amber-500" />
            <FormattedMessage
              id="notes.recording.paused"
              defaultMessage="Paused"
            />
          </span>
          <Button
            size="mini"
            onClick={handleResume}
            variant="default"
            className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3 z-10 relative"
            aria-label="Resume"
          >
            <MicIcon className="w-3.5 h-3.5" />
            <span className="text-xs">
              <FormattedMessage
                id="notes.recording.resume"
                defaultMessage="Resume"
              />
            </span>
          </Button>
          <Button
            size="mini"
            onClick={handleStop}
            variant="destructive"
            className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3 z-10 relative"
            aria-label="Stop"
          >
            <Square fill="white" className="w-3.5 h-3.5" />
            <span className="text-xs">
              <FormattedMessage
                id="notes.recording.stop"
                defaultMessage="Stop"
              />
            </span>
          </Button>
        </div>
      </div>
    );
  }

  // Processing queue state (not the big expansion)
  if (isProcessing) {
    return (
      <div
        className={cn(
          "absolute left-2 transition-all duration-300 ease-in-out",
          className
        )}
      >
        <Button
          size="mini"
          variant="default"
          disabled
          className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3 z-10 relative"
          aria-label="Processing..."
        >
          <LoaderCircle animate className="w-3.5 h-3.5" />
          <span className="text-xs">
            <FormattedMessage
              id="notes.recording.processing"
              defaultMessage="Processing..."
            />
          </span>
        </Button>
      </div>
    );
  }

  // Idle state: Start Scribe
  return (
    <div
      className={cn(
        "absolute left-2 transition-all duration-300 ease-in-out",
        className
      )}
    >
      <Button
        size="mini"
        onClick={handleStart}
        variant="default"
        disabled={isDisabled}
        className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3 z-10 relative"
        aria-label="Start Scribe"
      >
        <MicIcon className="w-3.5 h-3.5" />
        <span className="text-xs">
          <FormattedMessage
            id="notes.recording.startScribe"
            defaultMessage="Start Scribe"
          />
        </span>
      </Button>
    </div>
  );
};
